import React, { useEffect, useMemo, useState } from 'react'
import PopUp from '../../../ui/PopUp/PopUp'
import Icon from '../../../ui/Icon/Icon'
import Spinner from '../../../ui/Spinner/Spinner'
import getCollection from '../../../utils/db/getCollection'
import { CATALOG_TAXONOMY_COLLECTION } from '../../Products/components/CatalogTaxonomy/catalogTaxonomyConstants'
import CatalogTaxonomyNodePicker from '../../Products/productSchema/CatalogTaxonomyNodePicker'
import {
  buildCatalogTaxonomyNodePathLabel,
  buildCatalogTaxonomyNodePathSegments,
  buildCatalogTaxonomyNodesById,
} from '../../Products/productSchema/formatProductTaxonomySummary'
import {
  createPackagingCategorySelection,
  getPackagingCategoryNodeId,
  getPackagingCategoryPathLabel,
} from '../../../api/marketing/gifts/packagingCategorySelection'
import '../UtmBuilder/RelatedCategoryPicker.sass'
import styles from './PackagingCategoryPicker.module.scss'

function PackagingCategoryPicker({ value = null, onChange, label = 'Категорія пакування' }) {
  const [isOpen, setIsOpen] = useState(false)
  const [nodes, setNodes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const pathLabel = getPackagingCategoryPathLabel(value)
  const selectedNodeId = getPackagingCategoryNodeId(value)
  const hasValue = Boolean(pathLabel || selectedNodeId)

  const visibleNodes = useMemo(
    () => (nodes || []).filter((node) => node && node.isArchived !== true),
    [nodes]
  )
  const nodesById = useMemo(() => buildCatalogTaxonomyNodesById(visibleNodes), [visibleNodes])

  useEffect(() => {
    let isMounted = true

    setIsLoading(true)
    setLoadError('')

    getCollection({ path: CATALOG_TAXONOMY_COLLECTION, docIdName: 'id' })
      .then((docs) => {
        if (!isMounted) {
          return
        }

        setNodes(Array.isArray(docs) ? docs : [])
      })
      .catch((error) => {
        console.error('[PackagingCategoryPicker] Failed to load catalogTaxonomy:', error)

        if (!isMounted) {
          return
        }

        setLoadError('Не вдалося завантажити дерево категорій')
        setNodes([])
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const handleOpen = () => setIsOpen(true)

  const handleClear = (event) => {
    event.stopPropagation()
    onChange(null)
  }

  const handlePick = (node) => {
    const nextPathLabel = buildCatalogTaxonomyNodePathLabel(node.id, nodesById)
    const pathIds = buildCatalogTaxonomyNodePathSegments(node.id, nodesById).map((segment) => segment.id)

    onChange(
      createPackagingCategorySelection({
        node,
        pathLabel: nextPathLabel,
        pathIds,
      })
    )
    setIsOpen(false)
  }

  return (
    <div className="UtmBuilder-RelatedCategoryField">
      <div
        className={['Field', 'UtmBuilder-RelatedCategoryControl', hasValue ? 'Field_with_value' : ''].join(' ')}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleOpen()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <input
          type="text"
          className="UtmBuilder-RelatedCategoryInput"
          value={pathLabel}
          readOnly
          placeholder=""
        />
        {hasValue ? (
          <button
            type="button"
            className="UtmBuilder-RelatedCategoryClear"
            title="Очистити"
            onClick={handleClear}
          >
            <Icon name="times" weight="regular" size={12} />
          </button>
        ) : null}
        <span className="Field-Icon Field-Icon_position_right UtmBuilder-RelatedCategoryAngle">
          <Icon name="angle-down" />
        </span>
        <span className="Field-Bar" />
        <label>{label}</label>
      </div>

      {isOpen ? (
        <PopUp
          show
          title="Оберіть категорію пакування"
          close={() => setIsOpen(false)}
          className={styles.popup}
        >
          <div className={styles.popupBody}>
            {isLoading ? <Spinner type="popup" /> : null}
            {!isLoading && loadError ? <p className={styles.error}>{loadError}</p> : null}
            {!isLoading && !loadError && visibleNodes.length === 0 ? (
              <p className={styles.empty}>У catalogTaxonomy немає активних категорій</p>
            ) : null}
            {!isLoading && !loadError && visibleNodes.length > 0 ? (
              <CatalogTaxonomyNodePicker
                key={selectedNodeId || 'none'}
                nodes={visibleNodes}
                nodesById={nodesById}
                assignedIds={selectedNodeId ? [selectedNodeId] : []}
                mode="select"
                hint="Оберіть кінцеву категорію"
                focusNodeId={selectedNodeId}
                onPick={handlePick}
              />
            ) : null}
          </div>
        </PopUp>
      ) : null}
    </div>
  )
}

export default PackagingCategoryPicker
